import {
  ColorTokens,
  interaction,
  SizableText,
  styled,
  XStack,
  YStack,
  YStackProps,
} from '@mythical/ui'

import ContentThumbnail, { ContentThumbnailProps } from '../ContentThumbnail'
import { SmartLink } from 'app/components/primitive/SmartLink'
import { ColorEnum, mapColor } from 'app/utils/mapColors'

const CardWrapper = styled(YStack, {
  fs: 1,
  fd: 'column',
  jc: 'flex-start',
  ai: 'flex-start',
  width: '100%',
  br: '$4',
  ov: 'hidden',
})

const CardContent = styled(YStack, {
  gap: '$1.5',
  width: '100%',
  fs: 1,
  fg: 1,
  fd: 'column',
  jc: 'flex-start',
  ai: 'flex-start',
  px: '$2',
  py: '$3',
  $gtMd: {
    gap: '$2',
    p: '$3.5',
    pb: '$5',
  },
})

const CardHeading = styled(SizableText, {
  ff: '$body',
  size: '$6.5',
  fow: '600',
  height: 24,
  lineHeight: 24,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  $gtMd: {
    height: 28,
    lineHeight: 28,
  },
})

const CardText = styled(SizableText, {
  ff: '$body',
  size: '$2',
})

export interface ContentCardFeaturedProps {
  image: string
  title: string
  category: string
  href?: string
  backgroundColour?: ColorEnum | null
  foregroundColour?: ColorEnum | null
  seriesOverlayUrl?: string
  wrapperProps?: YStackProps
  thumbnailProps?: ContentThumbnailProps['wrapperProps']

  onNavigate?: () => void
}

const ContentCardFeatured = ({
  image,
  title,
  category,
  href,
  thumbnailProps,
  wrapperProps,
  onNavigate,
  backgroundColour,
  foregroundColour,
  seriesOverlayUrl,
}: ContentCardFeaturedProps) => {
  const bgColor: ColorTokens = backgroundColour
    ? mapColor(backgroundColour)
    : mapColor('background-secondary')

  const fgColor: ColorTokens = foregroundColour
    ? mapColor(foregroundColour)
    : mapColor('white')

  return (
    <CardWrapper bg={bgColor} {...wrapperProps}>
      <YStack width="100%" {...thumbnailProps}>
        <ContentThumbnail
          href={href}
          image={image}
          wrapperProps={{ bblr: '$0', bbrr: '$0' }}
          seriesOverlayUrl={seriesOverlayUrl}
          priority
          sizes="(min-width: 768px) calc((100vw - 300px) / 2), 100vw"
        />
      </YStack>
      <XStack jc="space-between" ai="flex-start" gap="$2" w="100%">
        <SmartLink
          Component={CardContent}
          href={href}
          onPress={onNavigate}
          {...interaction.opacity}
        >
          {category?.length > 1 && (
            <CardText color={fgColor}>{category}</CardText>
          )}
          <CardHeading color={fgColor} numberOfLines={1} ellipsizeMode="tail">
            {title}
          </CardHeading>
        </SmartLink>
      </XStack>
    </CardWrapper>
  )
}

export default ContentCardFeatured
